import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { withSnackbar } from 'notistack';
import { withNamespaces } from 'react-i18next';
import { observer } from 'mobx-react';
import AppBar from '@material-ui/core/AppBar';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import IconButton from '@material-ui/core/IconButton';
import Tooltip from '@material-ui/core/Tooltip';
import Hidden from '@material-ui/core/Hidden';
import withWidth from '@material-ui/core/withWidth';
import MuiThemeProvider from '@material-ui/core/styles/MuiThemeProvider';
import Refresh from '@material-ui/icons/Refresh';
import Save from '@material-ui/icons/Save';
import MoreVert from '@material-ui/icons/MoreVert';
import ViewList from '@material-ui/icons/ViewList';
import Help from '@material-ui/icons/HelpOutline';
import Map from '@material-ui/icons/Map';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import { lightTheme, darkTheme } from '../themes';
import { routePage } from '../store/route-page';
import {
  snackbarRouteSaveSuccess, snackbarRouteSaveError, snackbarRouteCannotBeEdited,
  snackbarNotFoundAddresses, snackbarNotGeoAddresses, snackbarRouteReloaded,
} from '../helpers/snackbars';
import { downloadRoutePage, uploadRoutePageOrder } from '../helpers/api';
import ReloadDialog from './dialogs/ReloadDialog';
import HelpDialog from './dialogs/HelpDialog';
import { MODES, mode } from '../store/mode';

class NavigationBar extends Component {
  constructor(props) {
    super(props);
    this.state = { isReloadOpen: false, isHelpOpen: false, overflowMenuAnchorEl: undefined };
    this.openReloadDialog = this.openReloadDialog.bind(this);
    this.closeReloadDialog = this.closeReloadDialog.bind(this);
    this.openHelpDialog = this.openHelpDialog.bind(this);
    this.closeHelpDialog = this.closeHelpDialog.bind(this);
    this.openOverflowMenu = this.openOverflowMenu.bind(this);
    this.closeOverflowMenu = this.closeOverflowMenu.bind(this);
    this.reload = this.reload.bind(this);
    this.reloadEverything = this.reloadEverything.bind(this);
    this.reloadAddresses = this.reloadAddresses.bind(this);
    this.save = this.save.bind(this);
  }

  openReloadDialog() {
    this.setState({ isReloadOpen: true, overflowMenuAnchorEl: undefined });
  }

  closeReloadDialog() {
    this.setState({ isReloadOpen: false });
  }

  openHelpDialog() {
    this.setState({ isHelpOpen: true, overflowMenuAnchorEl: undefined });
  }

  closeHelpDialog() {
    this.setState({ isHelpOpen: false });
  }

  openOverflowMenu(event) {
    this.setState({ overflowMenuAnchorEl: event.target });
  }

  closeOverflowMenu() {
    this.setState({ overflowMenuAnchorEl: undefined });
  }

  static onModeChangeClick() {
    if (mode.get() === MODES.MAP) {
      mode.set(MODES.LIST);
    } else {
      mode.set(MODES.MAP);
    }
  }

  reload({ keepOldOrder }) {
    const { t, enqueueSnackbar, uid, code } = this.props;
    downloadRoutePage({ uid, code, keepOldOrder })
      .then((downloadedRoutePage) => {
        const notFoundCount = downloadedRoutePage.notFoundStops.length;
        if (notFoundCount > 0) {
          snackbarNotFoundAddresses({ t, enqueueSnackbar, count: notFoundCount, onViewClick: () => this.openDialog('notFound') });
        }
        const notGeoCount = downloadedRoutePage.notGeolocatedStops.length;
        if (notGeoCount > 0) {
          snackbarNotGeoAddresses({ t, enqueueSnackbar, count: notGeoCount, onViewClick: () => this.openDialog('notGeo') });
        }
        if (!downloadedRoutePage.editable) {
          snackbarRouteCannotBeEdited({ t, enqueueSnackbar });
        }
        snackbarRouteReloaded({ t, enqueueSnackbar, code });
      })
      .catch(() => {});
    this.closeReloadDialog();
  }

  reloadEverything() {
    this.reload({ keepOldOrder: false });
  }

  reloadAddresses() {
    this.reload({ keepOldOrder: true });
  }

  save() {
    const { t, enqueueSnackbar, uid, code } = this.props;
    uploadRoutePageOrder({ uid, code })
      .then(() => snackbarRouteSaveSuccess({ t, enqueueSnackbar, code }))
      .catch(error => snackbarRouteSaveError({ t, enqueueSnackbar, code, errorCode: error.code }));
    this.setState({ overflowMenuAnchorEl: undefined });
  }

  render() {
    const { t } = this.props;
    const { isReloadOpen, isHelpOpen, overflowMenuAnchorEl } = this.state;
    const { agency, date, driver, code } = routePage;
    return (
      <MuiThemeProvider theme={darkTheme}>
        <AppBar position="static">
          <div style={{ padding: 4 }}>
            <Grid container alignItems="center" wrap="nowrap">
              <Grid item style={{ flexShrink: 0 }}>
                <img width="100px" src="/images/gls-logo-negative.svg" alt="GLS"/>
              </Grid>
              <Grid item style={{ flexShrink: 0, padding: '0 8px' }}>
                <Tooltip title={
                  <div>
                    {`${t('navigationBar.date')}: ${date}`}
                    <br/>
                    {`${t('navigationBar.driver')}: ${driver}`}
                    <br/>
                    {`${t('navigationBar.agency')}: ${agency}`}
                    <br/>
                    {`${t('navigationBar.route')}: ${code}`}
                  </div>
                }>
                  <Typography variant="body2" align="center" style={{ cursor: 'help' }}>
                    <strong>
                      {`${t('navigationBar.route')}`}
                      <br/>
                      <span style={{ borderBottom: '1px dotted' }}>{code}</span>
                    </strong>
                  </Typography>
                </Tooltip>
              </Grid>
              <Grid item style={{ flexGrow: 1 }}/>
              <Grid item style={{ flexShrink: 0 }}>
                <Hidden implementation="css" mdUp>
                  <Tooltip title={mode.get() === MODES.MAP ? t('buttons.viewList') : t('buttons.viewMap')}>
                    <IconButton onClick={NavigationBar.onModeChangeClick}>
                      {mode.get() === MODES.MAP ? <ViewList/> : <Map/>}
                    </IconButton>
                  </Tooltip>
                </Hidden>
              </Grid>
              <Grid item style={{ flexShrink: 0 }}>
                <Hidden implementation="css" smDown>
                  <Tooltip title={t('buttons.reloadRoute')}>
                    <IconButton onClick={this.openReloadDialog}>
                      <Refresh/>
                    </IconButton>
                  </Tooltip>
                </Hidden>
              </Grid>
              <Grid item style={{ flexShrink: 0 }}>
                <Hidden implementation="css" smDown>
                  <Tooltip title={t('buttons.help')}>
                    <IconButton onClick={this.openHelpDialog}>
                      <Help/>
                    </IconButton>
                  </Tooltip>
                </Hidden>
              </Grid>
              <Grid item style={{ flexShrink: 0 }}>
                <Hidden implementation="css" smDown>
                  <Tooltip title={t('buttons.save')}>
                    <IconButton disabled={!routePage.editable} onClick={this.save}>
                      <Save/>
                    </IconButton>
                  </Tooltip>
                </Hidden>
              </Grid>
              <Grid item style={{ flexShrink: 0 }}>
                <Hidden implementation="css" mdUp>
                  <Tooltip title={t('buttons.more')}>
                    <IconButton onClick={this.openOverflowMenu}>
                      <MoreVert/>
                    </IconButton>
                  </Tooltip>
                </Hidden>
              </Grid>
            </Grid>
          </div>
        </AppBar>
        <ReloadDialog open={isReloadOpen} onClose={this.closeReloadDialog} onReloadEverything={this.reloadEverything} onReloadAddresses={this.reloadAddresses}/>
        <HelpDialog open={isHelpOpen} onClose={this.closeHelpDialog}/>
        <MuiThemeProvider theme={lightTheme}>
          <Menu id="more-menu" anchorEl={overflowMenuAnchorEl} open={Boolean(overflowMenuAnchorEl)} onClose={this.closeOverflowMenu}>
            <MenuItem onClick={this.openReloadDialog}>
              <ListItemIcon>
                <Refresh/>
              </ListItemIcon>
              <ListItemText inset primary={t('buttons.reloadRoute')}/>
            </MenuItem>
            <MenuItem onClick={this.openHelpDialog}>
              <ListItemIcon>
                <Help/>
              </ListItemIcon>
              <ListItemText inset primary={t('buttons.help')}/>
            </MenuItem>
            <MenuItem disabled={!routePage.editable} onClick={this.save}>
              <ListItemIcon>
                <Save/>
              </ListItemIcon>
              <ListItemText inset primary={t('buttons.save')}/>
            </MenuItem>
          </Menu>
        </MuiThemeProvider>
      </MuiThemeProvider>
    );
  }
}

NavigationBar.propTypes = {
  enqueueSnackbar: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
  width: PropTypes.string.isRequired,
  uid: PropTypes.string.isRequired,
  code: PropTypes.string.isRequired,
};

export default withSnackbar(withNamespaces()(withWidth()(observer(NavigationBar))));



// WEBPACK FOOTER //
// ./src/components/NavigationBar.js